import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Search, Users, Phone, Wallet, CheckCircle2,
  Clock, X, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  AlertCircle, BadgeCheck, Award, RotateCcw, FileText, Hourglass, Landmark, Sparkles
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const ACCOUNT_LABELS = { nakit: 'Nakit', garanti_bbva: 'Garanti BBVA', enpara: '', bostanci: 'Bostancı', banka_havalesi: 'Havale' };

// Bir rezervasyonun hakemden tahsil edilmiş sayılıp sayılmayacağı.
// Yönetici "Tahsil Et" (referee_payment_collected) VEYA rapor "Teslim Edildi" (delivered) ise tahsil edilmiştir.
const isRefereeCollected = (res) =>
  res.referee_payment_collected === true || res.referee_report_status === 'delivered';

const formatTL = (n) => `₺${(parseFloat(n) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
};
const initials = (name) => (name || '').split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

import PageHeader from '../ui-pro/PageHeader';
import StatCard from '../ui-pro/StatCard';
const RefereesTab = ({
  referees = [],
  reservations = [],
  salons = [],
  customers = [],
  allReservationExpenses = [],
  fetchReferees,
  fetchReservations,
  fetchIncomes,
  fetchExpenses,
  fetchRefereesTotalBalance,
  onOpenReservationDetail,
  focusRefereeId = null,
  onFocusHandled
}) => {
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Selected
  const [selectedReferee, setSelectedReferee] = useState(null);
  const [editingReferee, setEditingReferee] = useState(null);

  // Month selector state (for stats)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Filters
  const [search, setSearch] = useState('');

  // Loading
  const [collectingId, setCollectingId] = useState(null);

  // Collect modal (ödeme yöntemi seçimi)
  const [showCollectModal, setShowCollectModal] = useState(false);
  const [collectTarget, setCollectTarget] = useState(null); // {type: 'single'|'all', reservation?: ...}
  const [collectMethod, setCollectMethod] = useState('nakit');

  // Havale modal (banka hesabı seçimi)
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null); // {reservation}
  const [transferMethod, setTransferMethod] = useState('garanti_bbva');
  const [transferringId, setTransferringId] = useState(null);

  // Forms
  const [newReferee, setNewReferee] = useState({ name: '', phone: '' });

  // Giriş bilgileri (credentials) formu
  const [credForm, setCredForm] = useState({ username: '', password: '' });
  const [savingCred, setSavingCred] = useState(false);
  const [refActive, setRefActive] = useState(true);
  const [togglingActive, setTogglingActive] = useState(false);
  const authConfig = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  useEffect(() => {
    if (selectedReferee) {
      setCredForm({ username: selectedReferee.username || '', password: '' });
      setRefActive(selectedReferee.is_active !== false);
    }
  }, [selectedReferee]);

  // Ana sayfadan gelen hakem odağı (bekleyen hakem tıklaması)
  useEffect(() => {
    if (!focusRefereeId) return;
    const ref = referees.find(r => r.id === focusRefereeId);
    if (ref) {
      setSelectedReferee(ref);
      setShowDetailModal(true);
      onFocusHandled?.();
    }
  }, [focusRefereeId, referees, onFocusHandled]);

  const trSlug = (s) => {
    const map = { 'ç':'c','ğ':'g','ı':'i','ö':'o','ş':'s','ü':'u','â':'a','î':'i','û':'u' };
    return (s || '').toLowerCase().split('').map(c => map[c] || c).join('').replace(/[^a-z0-9]/g, '');
  };

  const autoGeneratePassword = () => {
    const first = trSlug((selectedReferee?.name || 'hakem').trim().split(/\s+/)[0]) || 'hakem';
    const num = Math.floor(100 + Math.random() * 900);
    setCredForm((f) => ({ username: (f.username && f.username.trim()) ? f.username : first, password: `${first}${num}` }));
  };

  const toggleActive = async () => {
    if (!selectedReferee) return;
    const next = !refActive;
    setTogglingActive(true);
    setRefActive(next);
    try {
      await axios.put(`${API}/referees/${selectedReferee.id}/active`, { is_active: next }, authConfig);
      toast.success(next ? 'Hakem aktif yapıldı' : 'Hakem pasif yapıldı');
      fetchReferees && fetchReferees();
    } catch (e) {
      setRefActive(!next);
      toast.error('Durum güncellenemedi');
    } finally {
      setTogglingActive(false);
    }
  };

  const saveCredentials = async () => {
    if (!selectedReferee) return;
    if (!credForm.username.trim() || !credForm.password) {
      toast.error('Kullanıcı adı ve şifre zorunludur');
      return;
    }
    setSavingCred(true);
    try {
      await axios.post(`${API}/referees/${selectedReferee.id}/credentials`, {
        username: credForm.username.trim(), password: credForm.password
      }, authConfig);
      toast.success('Giriş bilgileri kaydedildi');
      setCredForm({ ...credForm, password: '' });
      fetchReferees && fetchReferees();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Oturum/token hatasını global interceptor yönetir; ham 'Token süresi dolmuş' mesajını gösterme
      if (typeof detail === 'string' && (detail.includes('Token') || detail.includes('token') || detail.includes('Yetkilendirme'))) return;
      toast.error(typeof detail === 'string' ? detail : 'Kaydedilemedi');
    } finally {
      setSavingCred(false);
    }
  };

  const [markingDelivered, setMarkingDelivered] = useState(false);
  const markPastDelivered = async () => {
    if (!window.confirm('Bugünden önceki TÜM hakemli maçlar "Teslim Edildi" olarak işaretlenip kilitlenecek. Yalnızca bugünden sonraki maçlar için hakemler rapor girebilecek. Devam edilsin mi?')) return;
    setMarkingDelivered(true);
    try {
      const res = await axios.post(`${API}/admin/referee-reports/mark-past-delivered`, {}, authConfig);
      toast.success(`${res.data.updated_count} geçmiş maç teslim edildi olarak işaretlendi`);
      fetchReservations && fetchReservations('all');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'string' && (detail.includes('Token') || detail.includes('Yetkilendirme'))) return;
      toast.error('İşlem başarısız');
    } finally {
      setMarkingDelivered(false);
    }
  };

  const unlockReport = async (res) => {
    try {
      await axios.put(`${API}/admin/referee-report/${res.id}`, {
        collected: res.referee_report_collected ?? null,
        expenses: res.referee_report_expenses || [],
        status: null
      }, authConfig);
      toast.success('Rapor kilidi açıldı, hakem tekrar düzenleyebilir');
      fetchReservations && fetchReservations('all');
    } catch (e) {
      toast.error('İşlem başarısız');
    }
  };

  // ===== Helpers =====
  const getCustomerName = (id) => customers.find(c => c.id === id)?.name || 'Müşteri';
  const getSalonName = (id) => salons.find(s => s.id === id)?.name || 'Salon';

  // Bir rezervasyon için net tahsil edilecek tutarı hesapla.
  // TEK KAYNAK: backend _compute_referee_net ile birebir → Toplam − Ücret − Gider − Kapora − Online − Volly.
  // Rapor gönderildiyse/teslim edildiyse hakemin BEYAN ettiği harcamalar, aksi halde rezervasyon giderleri esas alınır. Havale ise 0.
  // Rezervasyona girilen ekstra giderler (rapor gönderildiyse hakemin beyanı esas alınır)
  const calcExpenses = (res) => {
    if (res.referee_report_status === 'submitted' || res.referee_report_status === 'delivered') {
      return (res.referee_report_expenses || []).reduce((s, e) => s + (e.amount || 0), 0);
    }
    return allReservationExpenses
      .filter(e => e.reservation_id === res.id)
      .reduce((s, e) => s + (e.amount || 0), 0);
  };

  const calcNetAmount = (res) => {
    // Havale veya faturalı (+KDV) işlemde hakemin elinde nakit olmaz → teslim edeceği 0
    if (res.paid_via_transfer || res.invoice_issued) return 0;
    // Kapora HER ZAMAN düşülür (ödeme durumundan bağımsız): hakem yalnızca kalanı toplar/teslim eder.
    const kapora = res.paid_amount || 0;
    const online = res.referee_report_online_amount || 0;
    const volly = res.referee_report_volly_amount || 0;
    const expenses = calcExpenses(res);
    return Math.max(0, (res.total_amount || 0) - (res.referee_fee || 0) - expenses - kapora - online - volly);
  };

  // Bir hakem için istatistikleri hesapla (aya göre filtreli)
  // Önemli: "Bekleyen tahsilat" sadece müşterinin ödediği rezervasyonları içerir.
  // Müşteri henüz ödemediyse, hakem henüz parayı toplamamış demektir → tahsil bekleyene yazılmaz.
  const calcRefereeStats = (refereeId) => {
    const all = reservations.filter(r => r.referee_id === refereeId);
    const monthly = all.filter(r => {
      if (!r.date) return false;
      const d = new Date(r.date);
      return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
    });

    let pendingAll = 0;          // Müşteri ödedi & hakem henüz teslim etmedi
    let awaitingCustomer = 0;    // Müşteri henüz ödemedi (hakem topluyor)
    let collectedMonthly = 0;
    let pendingCount = 0;
    let awaitingCount = 0;
    let earnedMonthly = 0;       // Hakemin bu ay payı

    all.forEach(res => {
      const net = calcNetAmount(res);
      if (res.paid_via_transfer || res.invoice_issued) return; // havale/faturalı → hakeme yazılmaz
      if (isRefereeCollected(res)) return; // tahsil edilmiş (veya teslim edildi)
      if (res.payment_status === 'odendi') {
        pendingAll += net;
        pendingCount += 1;
      } else {
        awaitingCustomer += net;
        awaitingCount += 1;
      }
    });

    monthly.forEach(res => {
      const net = calcNetAmount(res);
      earnedMonthly += res.referee_fee || 0;
      if (isRefereeCollected(res)) collectedMonthly += net;
    });

    return {
      pendingAll,
      pendingCount,
      awaitingCustomer,
      awaitingCount,
      collectedMonthly,
      earnedMonthly,
      totalMatches: all.length,
      monthlyMatches: monthly.length
    };
  };

  // ===== Filtered list =====
  const filteredReferees = useMemo(() => {
    let list = referees;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(r =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.phone || '').includes(q)
      );
    }
    return list;
  }, [referees, search]);

  // ===== Global stats =====
  const globalStats = useMemo(() => {
    let totalPending = 0;
    let totalCollectedMonthly = 0;
    let monthlyAssignments = 0;
    let totalRefereeEarnings = 0;

    referees.forEach(ref => {
      const s = calcRefereeStats(ref.id);
      totalPending += s.pendingAll;
      totalCollectedMonthly += s.collectedMonthly;
      monthlyAssignments += s.monthlyMatches;
      totalRefereeEarnings += s.earnedMonthly;
    });

    return {
      totalReferees: referees.length,
      monthlyAssignments,
      totalPending,
      totalCollectedMonthly,
      totalRefereeEarnings
    };
  }, [referees, reservations, allReservationExpenses, selectedMonth, selectedYear]);

  // ===== Selected referee detail (assignments split) =====
  const selectedRefereeData = useMemo(() => {
    if (!selectedReferee) return null;
    const all = reservations.filter(r => r.referee_id === selectedReferee.id);

    const pending = [];           // Müşteri ödedi → hakemin teslim edeceği
    const awaiting = [];          // Müşteri henüz ödemedi → bekleniyor
    const collected = [];         // Hakemden tahsil edildi
    const transferred = [];       // Havale ile direkt bankaya geçti

    all.forEach(res => {
      const net = calcNetAmount(res);
      const enriched = {
        ...res,
        net_amount: net,
        expense_total: calcExpenses(res),
        kapora_amount: res.paid_amount || 0,
        online_amount: (res.referee_report_online_amount || 0) + (res.referee_report_volly_amount || 0),
        customer_name: getCustomerName(res.customer_id),
        salon_name: getSalonName(res.salon_id)
      };
      if (res.paid_via_transfer || res.invoice_issued) transferred.push(enriched);
      else if (isRefereeCollected(res)) collected.push(enriched);
      else if (res.payment_status === 'odendi') pending.push(enriched);
      else awaiting.push(enriched);
    });

    pending.sort((a, b) => new Date(b.date) - new Date(a.date));
    awaiting.sort((a, b) => new Date(b.date) - new Date(a.date));
    collected.sort((a, b) => new Date(b.date) - new Date(a.date));
    transferred.sort((a, b) => new Date(b.date) - new Date(a.date));

    const totalPending = pending.reduce((s, r) => s + r.net_amount, 0);
    const totalAwaiting = awaiting.reduce((s, r) => s + r.net_amount, 0);
    const totalCollected = collected.reduce((s, r) => s + r.net_amount, 0);
    const totalTransferred = transferred.reduce((s, r) => s + (r.total_amount || 0), 0);
    const totalEarned = all.reduce((s, r) => s + (r.referee_fee || 0), 0);

    return { pending, awaiting, collected, transferred, totalPending, totalAwaiting, totalCollected, totalTransferred, totalEarned, allCount: all.length };
  }, [selectedReferee, reservations, allReservationExpenses, customers, salons]);

  // Hakemin girdiği maç raporları (tahsilat + harcama + onay durumu)
  const refereeReports = useMemo(() => {
    if (!selectedReferee) return [];
    return reservations
      .filter(r => r.referee_id === selectedReferee.id && r.referee_report_status)
      .map(r => {
        const expTotal = (r.referee_report_expenses || []).reduce((s, e) => s + (e.amount || 0), 0);
        // Kapora HER ZAMAN düşülür (ödeme durumundan bağımsız): hakem yalnızca kalanı toplar/teslim eder.
        const kapora = r.paid_amount || 0;
        const online = r.referee_report_online_amount || 0;
        const volly = r.referee_report_volly_amount || 0;
        const net = (r.paid_via_transfer || r.invoice_issued) ? 0 : (r.total_amount || 0) - (r.referee_fee || 0) - expTotal - kapora - online - volly;
        const collected = r.referee_report_collected;
        return {
          ...r,
          _expTotal: expTotal,
          _kapora: kapora,
          _net: net,
          _mismatch: collected != null && Math.abs((collected || 0) - (r.total_amount || 0)) > 0.01,
        };
      })
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [selectedReferee, reservations]);

  // ===== Month nav =====
  const changeMonth = (dir) => {
    let nm = selectedMonth + dir, ny = selectedYear;
    if (nm < 0) { nm = 11; ny -= 1; }
    else if (nm > 11) { nm = 0; ny += 1; }
    setSelectedMonth(nm); setSelectedYear(ny);
  };
  const goToCurrent = () => { setSelectedMonth(new Date().getMonth()); setSelectedYear(new Date().getFullYear()); };
  const isCurrent = selectedMonth === new Date().getMonth() && selectedYear === new Date().getFullYear();

  // ===== CRUD =====
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newReferee.name?.trim()) { toast.error('Ad zorunludur'); return; }
    try {
      await axios.post(`${API}/referees`, {
        name: newReferee.name.trim(),
        phone: newReferee.phone || '',
        fee_per_match: 0  // Artık rezervasyonda manuel girilir
      });
      toast.success('Hakem eklendi');
      setShowAddModal(false);
      setNewReferee({ name: '', phone: '' });
      fetchReferees();
    } catch { toast.error('Hakem eklenemedi'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingReferee?.name?.trim()) { toast.error('Ad zorunludur'); return; }
    try {
      await axios.put(`${API}/referees/${editingReferee.id}`, {
        name: editingReferee.name.trim(),
        phone: editingReferee.phone || '',
        fee_per_match: 0
      });
      toast.success('Hakem güncellendi');
      setShowEditModal(false);
      setEditingReferee(null);
      fetchReferees();
    } catch { toast.error('Güncellenemedi'); }
  };

  const handleDelete = async (referee) => {
    if (!window.confirm(`"${referee.name}" hakemini silmek istiyor musunuz?`)) return;
    try {
      await axios.delete(`${API}/referees/${referee.id}`);
      toast.success('Hakem silindi');
      fetchReferees();
      fetchRefereesTotalBalance?.();
      setShowDetailModal(false);
    } catch { toast.error('Silinemedi'); }
  };

  // Tahsilat işlemi: rezervasyonun referee_payment_collected'ı true yapar + kasaya gelir ekler
  // Modal açar (ödeme yöntemi seçimi için)
  const openCollectSingle = (reservation) => {
    setCollectTarget({ type: 'single', reservation });
    setCollectMethod('nakit');
    setShowCollectModal(true);
  };

  const openCollectAll = () => {
    if (!selectedRefereeData?.pending.length) return;
    setCollectTarget({ type: 'all' });
    setCollectMethod('nakit');
    setShowCollectModal(true);
  };

  const confirmCollect = async () => {
    if (!collectTarget) return;
    try {
      if (collectTarget.type === 'single') {
        const res = collectTarget.reservation;
        setCollectingId(res.id);
        await axios.put(`${API}/reservations/${res.id}/payment-collected`, {
          collected: true,
          amount: res.net_amount,
          payment_method: collectMethod
        });
        toast.success(`Tahsil edildi: ${formatTL(res.net_amount)}`);
      } else {
        // Tümünü tahsil et
        await Promise.all(
          selectedRefereeData.pending.map(res =>
            axios.put(`${API}/reservations/${res.id}/payment-collected`, {
              collected: true,
              amount: res.net_amount,
              payment_method: collectMethod
            })
          )
        );
        toast.success(`${selectedRefereeData.pending.length} rezervasyon tahsil edildi`);
      }
      await Promise.all([fetchReservations('all'), fetchIncomes?.(), fetchRefereesTotalBalance?.()]);
      setShowCollectModal(false);
      setCollectTarget(null);
    } catch (e) {
      console.error(e);
      toast.error('İşlem başarısız');
    } finally {
      setCollectingId(null);
    }
  };

  // Tahsilatı geri al
  const handleUncollect = async (reservation) => {
    if (!window.confirm('Bu tahsilatı geri almak istiyor musunuz?\n\nKasadan ilgili gelir kaydı silinecek.')) return;
    setCollectingId(reservation.id);
    try {
      await axios.put(`${API}/reservations/${reservation.id}/payment-collected`, { collected: false });
      toast.success('Tahsilat geri alındı');
      await Promise.all([fetchReservations('all'), fetchIncomes?.(), fetchRefereesTotalBalance?.()]);
    } catch {
      toast.error('İşlem başarısız');
    } finally {
      setCollectingId(null);
    }
  };

  // ===== Havale (Bankaya Çek) işlemleri =====
  const openTransfer = (reservation) => {
    setTransferTarget({ reservation });
    setTransferMethod('garanti_bbva');
    setShowTransferModal(true);
  };

  const confirmTransfer = async () => {
    if (!transferTarget?.reservation) return;
    const res = transferTarget.reservation;
    setTransferringId(res.id);
    try {
      await axios.put(`${API}/reservations/${res.id}/bank-transfer`, {
        transfer: true,
        payment_method: transferMethod
      });
      toast.success(`Havale bankaya işlendi: ${formatTL(res.total_amount)}`);
      setShowTransferModal(false);
      setTransferTarget(null);
      await Promise.all([fetchReservations('all'), fetchIncomes?.(), fetchExpenses?.(), fetchRefereesTotalBalance?.()]);
    } catch (e) {
      console.error(e);
      toast.error('İşlem başarısız');
    } finally {
      setTransferringId(null);
    }
  };

  // Havaleyi geri al
  const handleUntransfer = async (reservation) => {
    if (!window.confirm('Havale işlemini geri almak istiyor musunuz?\n\nBankaya yazılan gelir ve hakem ücreti gideri silinecek; rezervasyon yeniden hakemin bekleyen listesine düşecek.')) return;
    setTransferringId(reservation.id);
    try {
      await axios.put(`${API}/reservations/${reservation.id}/bank-transfer`, { transfer: false });
      toast.success('Havale geri alındı');
      await Promise.all([fetchReservations('all'), fetchIncomes?.(), fetchExpenses?.(), fetchRefereesTotalBalance?.()]);
    } catch {
      toast.error('İşlem başarısız');
    } finally {
      setTransferringId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="referees-tab-root">
      <PageHeader
        icon={Award}
        title="Hakem Yönetimi"
        description="Hakem kayıtları, görev takibi ve müşteri tahsilatları"
        testid="referees-page-header"
        actions={
          <>
            <div className="flex items-center rounded-lg border border-[#E2E8F0] bg-white" data-testid="referees-month-selector">
              <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)} className="h-9 px-2 text-slate-600"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="px-2 text-[13px] font-semibold text-slate-800 min-w-[104px] text-center">{MONTHS[selectedMonth]} {selectedYear}</div>
              <Button variant="ghost" size="sm" onClick={() => changeMonth(1)} className="h-9 px-2 text-slate-600"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            {!isCurrent && (
              <Button variant="outline" size="sm" onClick={goToCurrent} className="h-9 text-[12px]">
                <CalendarIcon className="w-3.5 h-3.5 mr-1" /> Bu Aya Dön
              </Button>
            )}
            <Button onClick={markPastDelivered} disabled={markingDelivered} variant="outline" className="h-9 text-[12px]" data-testid="mark-past-delivered-btn" title="Geçmiş maçları toplu teslim edildi işaretle">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {markingDelivered ? 'İşleniyor...' : 'Geçmişi Teslim Edildi Yap'}
            </Button>
            <Button onClick={() => setShowAddModal(true)} className="h-9 bg-[#176B91] hover:bg-[#145a7a]" data-testid="add-referee-btn">
              <Plus className="w-4 h-4 mr-1" /> Yeni Hakem
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Award} tone="amber" testid="kpi-total-referees" label="Toplam Hakem" value={globalStats.totalReferees} sub="kayıtlı hakem" />
        <StatCard icon={CalendarIcon} tone="sky" testid="kpi-monthly-assignments" label="Bu Ay Görev" value={globalStats.monthlyAssignments} sub={`${MONTHS[selectedMonth]} ${selectedYear}`} />
        <StatCard icon={AlertCircle} tone="rose" testid="kpi-total-pending" label="Bekleyen Tahsilat" value={formatTL(globalStats.totalPending)} sub="hakemden alınacak" />
        <StatCard icon={CheckCircle2} tone="emerald" testid="kpi-collected" label="Bu Ay Tahsil Edilen" value={formatTL(globalStats.totalCollectedMonthly)} sub="teslim alınan" />
      </div>

      {/* ===== Filter Bar ===== */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Hakem adı veya telefon ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="referee-search-input" />
            </div>
            {search && (
              <Button variant="ghost" size="sm" onClick={() => setSearch('')} className="text-slate-500 h-9">
                <X className="w-4 h-4 mr-1" /> Temizle
              </Button>
            )}
            <div className="ml-auto text-sm text-slate-500">
              <span className="font-semibold text-slate-700">{filteredReferees.length}</span> / {referees.length} hakem
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Referee Cards ===== */}
      {filteredReferees.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-slate-400">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{referees.length === 0 ? 'Henüz hakem eklenmemiş' : 'Aramaya uygun hakem yok'}</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="referees-cards-grid">
          {filteredReferees.map(ref => {
            const stats = calcRefereeStats(ref.id);
            const hasPending = stats.pendingCount > 0;
            return (
              <Card
                key={ref.id}
                className="hover:shadow-xl transition-all cursor-pointer relative overflow-hidden"
                onClick={() => { setSelectedReferee(ref); setShowDetailModal(true); }}
                data-testid={`referee-card-${ref.id}`}
              >
                {hasPending && (
                  <div className="absolute top-0 right-0 bg-rose-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl">
                    {stats.pendingCount} bekleyen
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold shadow-md">
                      {initials(ref.name)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{ref.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {ref.phone || 'Telefon yok'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-slate-50 rounded p-2">
                      <p className="text-[10px] text-slate-500 uppercase">Toplam Maç</p>
                      <p className="font-bold text-slate-800 text-sm">{stats.totalMatches}</p>
                    </div>
                    <div className="bg-blue-50 rounded p-2">
                      <p className="text-[10px] text-blue-600 uppercase">Bu Ay</p>
                      <p className="font-bold text-blue-800 text-sm">{stats.monthlyMatches} maç</p>
                    </div>
                  </div>

                  {/* Bekleyen Tahsilat */}
                  <div className={`rounded-lg p-2.5 border-l-4 ${hasPending ? 'bg-rose-50 border-l-rose-500' : 'bg-emerald-50 border-l-emerald-500'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-[10px] uppercase font-medium ${hasPending ? 'text-rose-600' : 'text-emerald-600'}`}>
                          {hasPending ? 'Bekleyen Tahsilat' : 'Tahsilat Tamam'}
                        </p>
                        <p className={`text-lg font-bold ${hasPending ? 'text-rose-700' : 'text-emerald-700'}`}>
                          {formatTL(stats.pendingAll)}
                        </p>
                      </div>
                      {hasPending ? <Clock className="w-5 h-5 text-rose-400" /> : <BadgeCheck className="w-5 h-5 text-emerald-400" />}
                    </div>
                  </div>

                  {stats.awaitingCount > 0 && (
                    <div className="mt-2 rounded p-2 bg-amber-50 border border-amber-200 flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1 text-amber-700">
                        <Hourglass className="w-3.5 h-3.5" /> Müşteri ödemiyor
                      </span>
                      <span className="font-semibold text-amber-700">
                        {formatTL(stats.awaitingCustomer)} • {stats.awaitingCount} rez.
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ============ ADD MODAL ============ */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md" data-testid="add-referee-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Award className="w-5 h-5 text-orange-600" /> Yeni Hakem</DialogTitle>
            <DialogDescription>Yeni hakem ekleyin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdd} className="space-y-3">
            <div><Label>Ad Soyad *</Label><Input value={newReferee.name} onChange={(e) => setNewReferee({...newReferee, name: e.target.value})} required data-testid="add-referee-name-input" /></div>
            <div><Label>Telefon</Label><Input value={newReferee.phone} onChange={(e) => setNewReferee({...newReferee, phone: e.target.value})} placeholder="0532..." /></div>
            <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-700">
              <strong>Bilgi:</strong> Hakem ücreti rezervasyon oluşturulurken her maç için ayrı ayrı belirlenir.
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="flex-1">İptal</Button>
              <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="add-referee-submit-btn">Kaydet</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ EDIT MODAL ============ */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md" data-testid="edit-referee-modal">
          <DialogHeader><DialogTitle>Hakem Düzenle</DialogTitle></DialogHeader>
          {editingReferee && (
            <form onSubmit={handleUpdate} className="space-y-3">
              <div><Label>Ad Soyad *</Label><Input value={editingReferee.name || ''} onChange={(e) => setEditingReferee({...editingReferee, name: e.target.value})} required /></div>
              <div><Label>Telefon</Label><Input value={editingReferee.phone || ''} onChange={(e) => setEditingReferee({...editingReferee, phone: e.target.value})} /></div>
              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">İptal</Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">Güncelle</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ DETAIL MODAL ============ */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="referee-detail-modal">
          {selectedReferee && selectedRefereeData && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {initials(selectedReferee.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="text-xl">{selectedReferee.name}</DialogTitle>
                    <DialogDescription className="flex items-center gap-2 mt-0.5">
                      <Phone className="w-3 h-3" /> {selectedReferee.phone || 'Telefon yok'}
                      <span className="text-slate-300">•</span>
                      <span>{selectedRefereeData.allCount} toplam maç</span>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              {/* Üst KPI'lar */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
                <Card className={`border-l-4 ${selectedRefereeData.totalPending > 0 ? 'border-l-rose-500 bg-rose-50' : 'border-l-emerald-500 bg-emerald-50'}`}>
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Bekleyen Tahsilat</p>
                    <p className={`text-lg font-bold ${selectedRefereeData.totalPending > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                      {formatTL(selectedRefereeData.totalPending)}
                    </p>
                    <p className="text-[10px] text-slate-500">{selectedRefereeData.pending.length} rezervasyon</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-amber-500 bg-amber-50">
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Müşteri Ödemeyi Bekliyor</p>
                    <p className="text-lg font-bold text-amber-700">{formatTL(selectedRefereeData.totalAwaiting)}</p>
                    <p className="text-[10px] text-slate-500">{selectedRefereeData.awaiting.length} rezervasyon</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-emerald-500 bg-emerald-50">
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Tahsil Edilen</p>
                    <p className="text-lg font-bold text-emerald-700">{formatTL(selectedRefereeData.totalCollected)}</p>
                    <p className="text-[10px] text-slate-500">{selectedRefereeData.collected.length} rezervasyon</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-blue-500 bg-blue-50">
                  <CardContent className="p-3">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Hakem Kazancı</p>
                    <p className="text-lg font-bold text-blue-700">{formatTL(selectedRefereeData.totalEarned)}</p>
                    <p className="text-[10px] text-slate-500">{selectedRefereeData.allCount} toplam maç</p>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="pending" data-testid="referee-detail-tabs">
                <TabsList className="grid w-full grid-cols-6 h-auto">
                  <TabsTrigger value="pending" className="gap-1 py-2" data-testid="tab-pending-collections">
                    <Clock className="w-4 h-4" /> Bekleyen
                    {selectedRefereeData.pending.length > 0 && <Badge className="ml-1 h-5 text-[10px] bg-rose-500 hover:bg-rose-500">{selectedRefereeData.pending.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="awaiting" className="gap-1 py-2" data-testid="tab-awaiting-payment">
                    <Hourglass className="w-4 h-4" /> Müşteri Ödemedi
                    {selectedRefereeData.awaiting.length > 0 && <Badge className="ml-1 h-5 text-[10px] bg-amber-500 hover:bg-amber-500">{selectedRefereeData.awaiting.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="transferred" className="gap-1 py-2" data-testid="tab-transferred">
                    <Landmark className="w-4 h-4" /> Havale
                    {selectedRefereeData.transferred.length > 0 && <Badge className="ml-1 h-5 text-[10px] bg-blue-500 hover:bg-blue-500">{selectedRefereeData.transferred.length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="collected" className="gap-1 py-2" data-testid="tab-collected">
                    <CheckCircle2 className="w-4 h-4" /> Tahsil ({selectedRefereeData.collected.length})
                  </TabsTrigger>
                  <TabsTrigger value="reports" className="gap-1 py-2" data-testid="tab-referee-reports">
                    <FileText className="w-4 h-4" /> Raporlar
                    {refereeReports.filter(r => r.referee_report_status === 'submitted').length > 0 && <Badge className="ml-1 h-5 text-[10px] bg-emerald-500 hover:bg-emerald-500">{refereeReports.filter(r => r.referee_report_status === 'submitted').length}</Badge>}
                  </TabsTrigger>
                  <TabsTrigger value="info" className="gap-1 py-2" data-testid="tab-referee-info">
                    <FileText className="w-4 h-4" /> Bilgiler
                  </TabsTrigger>
                </TabsList>

                {/* === Tab: Bekleyen Tahsilatlar === */}
                <TabsContent value="pending" className="space-y-3 mt-3">
                  {selectedRefereeData.pending.length === 0 ? (
                    <Card><CardContent className="p-8 text-center">
                      <BadgeCheck className="w-12 h-12 mx-auto mb-2 text-emerald-300" />
                      <p className="text-slate-500 font-medium">Bekleyen tahsilat yok</p>
                      <p className="text-xs text-slate-400 mt-1">Tüm rezervasyonlar tahsil edilmiş ✓</p>
                    </CardContent></Card>
                  ) : (
                    <>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-sm text-amber-900">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>Bu hakemden <span className="font-bold">{formatTL(selectedRefereeData.totalPending)}</span> tutarında tahsilat yapılacak.</span>
                        </div>
                        <Button onClick={openCollectAll} className="bg-emerald-600 hover:bg-emerald-700" size="sm" data-testid="collect-all-btn">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Tümünü Tahsil Et
                        </Button>
                      </div>

                      <Card>
                        <CardContent className="p-0">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b">
                              <tr className="text-left">
                                <th className="px-3 py-2 font-medium text-slate-600">Tarih</th>
                                <th className="px-2 py-2 font-medium text-slate-600">Müşteri / Salon</th>
                                <th className="px-2 py-2 font-medium text-slate-600 text-right">Toplam</th>
                                <th className="px-2 py-2 font-medium text-slate-600 text-right">Hakem Payı</th>
                                <th className="px-2 py-2 font-medium text-slate-600 text-right">Gider</th>
                                <th className="px-2 py-2 font-medium text-slate-600 text-right">Kapora</th>
                                <th className="px-2 py-2 font-medium text-slate-600 text-right">Net (Alınacak)</th>
                                <th className="px-2 py-2 font-medium text-slate-600 text-center w-[200px]">İşlem</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedRefereeData.pending.map(res => (
                                <tr key={res.id} className="border-b hover:bg-blue-50/60 cursor-pointer" data-testid={`pending-row-${res.id}`} title="Detayları görüntüle" onClick={() => onOpenReservationDetail && onOpenReservationDetail(res)}>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    <div className="font-medium">{formatDate(res.date)}</div>
                                    <div className="text-xs text-slate-500">{(res.start_time || '').slice(0,5)}-{(res.end_time || '').slice(0,5)}</div>
                                  </td>
                                  <td className="px-2 py-2">
                                    <div className="font-medium text-slate-800 truncate max-w-[180px]">{res.customer_name}</div>
                                    <div className="text-xs text-slate-500 truncate max-w-[180px]">{res.salon_name}</div>
                                    {res.referee_report_status === 'submitted' && (
                                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" title="Hakem raporunu gönderdi. Teslim alınca Tahsil Et.">
                                        <CheckCircle2 className="w-3 h-3" /> Hakem raporladı
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-2 py-2 text-right">{formatTL(res.total_amount)}</td>
                                  <td className="px-2 py-2 text-right text-blue-600">{formatTL(res.referee_fee || 0)}</td>
                                  <td className="px-2 py-2 text-right" data-testid={`pending-expense-${res.id}`}>
                                    {res.expense_total > 0
                                      ? <span className="text-rose-600 font-medium">−{formatTL(res.expense_total)}</span>
                                      : <span className="text-slate-400">-</span>}
                                  </td>
                                  <td className="px-2 py-2 text-right">
                                    {res.kapora_amount > 0
                                      ? <span className="text-amber-700">−{formatTL(res.kapora_amount)}</span>
                                      : <span className="text-slate-400">-</span>}
                                  </td>
                                  <td className="px-2 py-2 text-right">
                                    <span className="font-bold text-rose-700">{formatTL(res.net_amount)}</span>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); openCollectSingle(res); }}
                                        disabled={collectingId === res.id || transferringId === res.id}
                                        className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                        data-testid={`collect-btn-${res.id}`}
                                      >
                                        {collectingId === res.id ? '...' : <><CheckCircle2 className="w-3 h-3 mr-1" /> Tahsil Et</>}
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => { e.stopPropagation(); openTransfer(res); }}
                                        disabled={collectingId === res.id || transferringId === res.id}
                                        className="h-7 text-xs text-blue-700 border-blue-300 hover:bg-blue-50"
                                        title="Müşteri havale ile ödedi → bankaya çek"
                                        data-testid={`transfer-btn-${res.id}`}
                                      >
                                        {transferringId === res.id ? '...' : <><Landmark className="w-3 h-3 mr-1" /> Havale</>}
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="bg-rose-50 border-t-2">
                              <tr className="font-bold text-rose-900">
                                <td colSpan={2} className="px-3 py-2 text-right">Toplam:</td>
                                <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.pending.reduce((s, r) => s + (r.total_amount || 0), 0))}</td>
                                <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.pending.reduce((s, r) => s + (r.referee_fee || 0), 0))}</td>
                                <td className="px-2 py-2 text-right" data-testid="pending-expense-total">{formatTL(selectedRefereeData.pending.reduce((s, r) => s + (r.expense_total || 0), 0))}</td>
                                <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.pending.reduce((s, r) => s + (r.kapora_amount || 0), 0))}</td>
                                <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.totalPending)}</td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </TabsContent>

                {/* === Tab: Müşteri Ödemedi (Bekliyor) === */}
                <TabsContent value="awaiting" className="space-y-3 mt-3">
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="p-3 text-xs text-amber-900 leading-relaxed">
                      <AlertCircle className="w-4 h-4 inline mr-1 -mt-0.5" />
                      Bu rezervasyonlarda <strong>müşteri henüz ödeme yapmadı</strong>. Hakem parayı toplayamadığı için tahsil bekleyen tutara yazılmıyor. Müşteri ödedikten sonra rezervasyon otomatik olarak <strong>Bekleyen Tahsilat</strong> sekmesine geçecek.
                    </CardContent>
                  </Card>
                  {selectedRefereeData.awaiting.length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-slate-400">
                      <Hourglass className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Müşteri ödemesi bekleyen rezervasyon yok</p>
                    </CardContent></Card>
                  ) : (
                    <Card><CardContent className="p-0">
                      <div className="overflow-x-auto max-h-72">
                        <table className="w-full text-sm">
                          <thead className="bg-amber-100 text-slate-700 sticky top-0 border-b">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-medium">Tarih / Saat</th>
                              <th className="px-2 py-2 font-medium">Müşteri</th>
                              <th className="px-2 py-2 font-medium">Salon</th>
                              <th className="px-2 py-2 font-medium text-right">Toplam</th>
                              <th className="px-2 py-2 font-medium text-right">Gider</th>
                              <th className="px-2 py-2 font-medium text-right">Net (Hakemden)</th>
                              <th className="px-2 py-2 font-medium text-center">Durum</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRefereeData.awaiting.map(res => (
                              <tr key={res.id} className="border-b hover:bg-amber-50/50 cursor-pointer" title="Detayları görüntüle" onClick={() => onOpenReservationDetail && onOpenReservationDetail(res)}>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div className="font-medium">{formatDate(res.date)}</div>
                                  <div className="text-xs text-slate-500">{res.start_time}–{res.end_time}</div>
                                </td>
                                <td className="px-2 py-2">{res.customer_name}</td>
                                <td className="px-2 py-2 text-xs">{res.salon_name}</td>
                                <td className="px-2 py-2 text-right font-mono">{formatTL(res.total_amount || 0)}</td>
                                <td className="px-2 py-2 text-right font-mono" data-testid={`awaiting-expense-${res.id}`}>
                                  {res.expense_total > 0
                                    ? <span className="text-rose-600">−{formatTL(res.expense_total)}</span>
                                    : <span className="text-slate-400">-</span>}
                                </td>
                                <td className="px-2 py-2 text-right font-mono font-semibold text-amber-700">{formatTL(res.net_amount)}</td>
                                <td className="px-2 py-2 text-center">
                                  <Badge className="bg-amber-200 text-amber-800 border-0 text-[10px]">Müşteri ödemedi</Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent></Card>
                  )}
                </TabsContent>

                {/* === Tab: Havale (Bankaya Geçti) === */}
                <TabsContent value="transferred" className="space-y-3 mt-3">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-3 text-xs text-blue-900 leading-relaxed">
                      <Landmark className="w-4 h-4 inline mr-1 -mt-0.5" />
                      Bu rezervasyonların ödemesi <strong>müşteri tarafından havale ile direkt bankaya</strong> yapılmıştır. Tam tutar kasaya/bankaya işlenmiş, hakem ücreti ayrı gider olarak kaydedilmiştir. Bu rezervasyonlar hakemin <strong>teslim edeceği tutara dahil değildir</strong>.
                    </CardContent>
                  </Card>
                  {selectedRefereeData.transferred.length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-slate-400">
                      <Landmark className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Havale ile ödenen rezervasyon yok</p>
                    </CardContent></Card>
                  ) : (
                    <Card>
                      <CardContent className="p-0">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-medium text-slate-600">Tarih</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Müşteri / Salon</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Bankaya Geçen</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-center">Hesap</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-center w-[110px]">İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRefereeData.transferred.map(res => (
                              <tr key={res.id} className="border-b hover:bg-blue-50/60 cursor-pointer" data-testid={`transferred-row-${res.id}`} title="Detayları görüntüle" onClick={() => onOpenReservationDetail && onOpenReservationDetail(res)}>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div className="font-medium">{formatDate(res.date)}</div>
                                  <div className="text-xs text-slate-500">{(res.start_time || '').slice(0,5)}-{(res.end_time || '').slice(0,5)}</div>
                                </td>
                                <td className="px-2 py-2">
                                  <div className="font-medium text-slate-800 truncate max-w-[200px]">{res.customer_name}</div>
                                  <div className="text-xs text-slate-500 truncate max-w-[200px]">{res.salon_name}</div>
                                </td>
                                <td className="px-2 py-2 text-right font-bold text-blue-700">{formatTL(res.total_amount)}</td>
                                <td className="px-2 py-2 text-center">
                                  <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{ACCOUNT_LABELS[res.transfer_payment_method] || res.transfer_payment_method || 'Banka'}</Badge>
                                </td>
                                <td className="px-2 py-2 text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); handleUntransfer(res); }}
                                    disabled={transferringId === res.id}
                                    className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                                    title="Havaleyi geri al"
                                    data-testid={`untransfer-btn-${res.id}`}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" /> Geri Al
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-blue-50 border-t-2">
                            <tr className="font-bold text-blue-900">
                              <td colSpan={2} className="px-3 py-2 text-right">Toplam Havale:</td>
                              <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.totalTransferred)}</td>
                              <td colSpan={2}></td>
                            </tr>
                          </tfoot>
                        </table>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* === Tab: Tahsil Edilenler === */}
                <TabsContent value="collected" className="space-y-3 mt-3">
                  {selectedRefereeData.collected.length === 0 ? (
                    <Card><CardContent className="p-8 text-center text-slate-400">
                      <Wallet className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>Henüz tahsilat yapılmamış</p>
                    </CardContent></Card>
                  ) : (
                    <Card>
                      <CardContent className="p-0">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 border-b">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-medium text-slate-600">Tarih</th>
                              <th className="px-2 py-2 font-medium text-slate-600">Müşteri / Salon</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Toplam</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Hakem Payı</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Gider</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Kapora</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-right">Tahsil Edilen (Net)</th>
                              <th className="px-2 py-2 font-medium text-slate-600 text-center w-[110px]">İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRefereeData.collected.map(res => (
                              <tr key={res.id} className="border-b hover:bg-blue-50/60 cursor-pointer" data-testid={`collected-row-${res.id}`} title="Detayları görüntüle" onClick={() => onOpenReservationDetail && onOpenReservationDetail(res)}>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <div className="font-medium">{formatDate(res.date)}</div>
                                  <div className="text-xs text-slate-500">{(res.start_time || '').slice(0,5)}-{(res.end_time || '').slice(0,5)}</div>
                                </td>
                                <td className="px-2 py-2">
                                  <div className="font-medium text-slate-800 truncate max-w-[200px]">{res.customer_name}</div>
                                  <div className="text-xs text-slate-500 truncate max-w-[200px]">{res.salon_name}</div>
                                </td>
                                <td className="px-2 py-2 text-right">{formatTL(res.total_amount)}</td>
                                <td className="px-2 py-2 text-right text-blue-600">{formatTL(res.referee_fee || 0)}</td>
                                <td className="px-2 py-2 text-right" data-testid={`collected-expense-${res.id}`}>
                                  {res.expense_total > 0
                                    ? <span className="text-rose-600 font-medium">−{formatTL(res.expense_total)}</span>
                                    : <span className="text-slate-400">-</span>}
                                </td>
                                <td className="px-2 py-2 text-right">
                                  {res.kapora_amount > 0
                                    ? <span className="text-amber-700">−{formatTL(res.kapora_amount)}</span>
                                    : <span className="text-slate-400">-</span>}
                                </td>
                                <td className="px-2 py-2 text-right font-bold text-emerald-700">{formatTL(res.net_amount)}</td>
                                <td className="px-2 py-2 text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => { e.stopPropagation(); handleUncollect(res); }}
                                    disabled={collectingId === res.id}
                                    className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                                    title="Tahsilatı geri al"
                                    data-testid={`uncollect-btn-${res.id}`}
                                  >
                                    <RotateCcw className="w-3 h-3 mr-1" /> Geri Al
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-emerald-50 border-t-2">
                            <tr className="font-bold text-emerald-900">
                              <td colSpan={2} className="px-3 py-2 text-right">Toplam Tahsilat:</td>
                              <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.collected.reduce((s, r) => s + (r.total_amount || 0), 0))}</td>
                              <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.collected.reduce((s, r) => s + (r.referee_fee || 0), 0))}</td>
                              <td className="px-2 py-2 text-right" data-testid="collected-expense-total">
                                {formatTL(selectedRefereeData.collected.reduce((s, r) => s + (r.expense_total || 0), 0))}
                              </td>
                              <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.collected.reduce((s, r) => s + (r.kapora_amount || 0), 0))}</td>
                              <td className="px-2 py-2 text-right">{formatTL(selectedRefereeData.totalCollected)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* === Tab: Hakem Raporları (admin görünümü) === */}
                <TabsContent value="reports" className="space-y-3 mt-3" data-testid="referee-reports-content">
                  {refereeReports.length === 0 ? (
                    <p className="text-slate-500 text-center py-8 text-sm">Bu hakem henüz maç raporu girmedi.</p>
                  ) : (
                    <div className="space-y-2">
                      {refereeReports.map((r) => (
                        <Card key={r.id} className={r._mismatch ? 'border-rose-300' : ''}>
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-semibold text-slate-800 text-sm">{getCustomerName(r.customer_id)} <span className="text-slate-400 font-normal">• {formatDate(r.date)}</span></p>
                                <p className="text-xs text-slate-500">{getSalonName(r.salon_id)}</p>
                              </div>
                              {r.referee_report_status === 'delivered'
                                ? <Badge className="bg-teal-500 hover:bg-teal-500 gap-1"><CheckCircle2 className="w-3 h-3" /> Teslim Edildi</Badge>
                                : r.referee_report_status === 'submitted'
                                ? <Badge className="bg-emerald-500 hover:bg-emerald-500 gap-1"><CheckCircle2 className="w-3 h-3" /> Gönderildi</Badge>
                                : <Badge variant="outline" className="border-amber-400 text-amber-600 gap-1"><Clock className="w-3 h-3" /> Taslak</Badge>}
                            </div>
                            <div className="grid grid-cols-4 gap-2 mt-2 text-center text-xs">
                              <div><p className="text-slate-400">Maç Tutarı</p><p className="font-semibold">{formatTL(r.total_amount)}</p></div>
                              <div><p className="text-slate-400">Beyan Tahsilat</p><p className={`font-semibold ${r._mismatch ? 'text-rose-600' : 'text-slate-700'}`}>{r.referee_report_collected != null ? formatTL(r.referee_report_collected) : '—'}</p></div>
                              <div><p className="text-slate-400">Harcama</p><p className="font-semibold text-red-600">{formatTL(r._expTotal)}</p></div>
                              <div><p className="text-slate-400">Teslim Net</p><p className="font-bold text-orange-600">{formatTL(r._net)}</p></div>
                            </div>
                            {(r.referee_report_expenses || []).length > 0 && (
                              <div className="mt-2 text-xs text-slate-600 bg-slate-50 rounded p-2">
                                <span className="font-medium">Harcamalar: </span>
                                {(r.referee_report_expenses || []).map((e, i) => (
                                  <span key={i}>{e.description} ({formatTL(e.amount)}){i < r.referee_report_expenses.length - 1 ? ', ' : ''}</span>
                                ))}
                              </div>
                            )}
                            {(r.referee_report_online_amount > 0 || r.referee_report_volly_amount > 0) && (
                              <div className="mt-2 text-xs text-cyan-700 bg-cyan-50 rounded p-2 space-y-0.5">
                                {r.referee_report_online_amount > 0 && (
                                  <div><span className="font-medium">Online Ödeme:</span> {formatTL(r.referee_report_online_amount)} — {r.referee_report_online_name || '(isim yok)'}</div>
                                )}
                                {r.referee_report_volly_amount > 0 && (
                                  <div><span className="font-medium">Volly Puan Ödemesi:</span> {formatTL(r.referee_report_volly_amount)} — {r.referee_report_volly_name || '(isim yok)'}</div>
                                )}
                              </div>
                            )}
                            {r._mismatch && (
                              <p className="mt-2 text-xs text-rose-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Beyan edilen tahsilat maç tutarından farklı!</p>
                            )}
                            {(r.referee_report_status === 'submitted' || r.referee_report_status === 'delivered') && (
                              <div className="mt-2 flex justify-end">
                                <Button size="sm" variant="outline" onClick={() => unlockReport(r)} data-testid={`unlock-report-${r.id}`} className="gap-1 text-xs">
                                  <RotateCcw className="w-3 h-3" /> Kilidi Aç (Hakem Düzenlesin)
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* === Tab: Bilgiler === */}
                <TabsContent value="info" className="space-y-3 mt-3">
                  <Card><CardContent className="p-4 space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Ad Soyad</p>
                        <p className="font-semibold text-slate-800">{selectedReferee.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Telefon</p>
                        <p className="font-semibold text-slate-800">{selectedReferee.phone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Toplam Maç</p>
                        <p className="font-semibold text-slate-800">{selectedRefereeData.allCount}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">Toplam Kazanç (Hakem Payı)</p>
                        <p className="font-bold text-blue-700">{formatTL(selectedRefereeData.totalEarned)}</p>
                      </div>
                    </div>
                  </CardContent></Card>

                  {/* Giriş Bilgileri (Hakem Paneli erişimi) */}
                  <Card data-testid="referee-credentials-card"><CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <BadgeCheck className="w-4 h-4 text-blue-600" />
                      <p className="font-semibold text-slate-800 text-sm">Hakem Paneli Giriş Bilgileri</p>
                      {selectedReferee.username && <Badge className="bg-emerald-500 hover:bg-emerald-500 text-[10px]">Aktif</Badge>}
                    </div>
                    <p className="text-xs text-slate-500">Hakem bu bilgilerle giriş yapıp atandığı maçları görür, tahsilat/harcama girer ve onaya gönderir.</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Kullanıcı Adı</Label>
                        <Input value={credForm.username} onChange={(e) => setCredForm({ ...credForm, username: e.target.value })} placeholder="ör: ahmet.hakem" data-testid="referee-cred-username" />
                      </div>
                      <div>
                        <Label className="text-xs">Şifre {selectedReferee.username && <span className="text-slate-400">(değiştirmek için doldurun)</span>}</Label>
                        <Input type="text" value={credForm.password} onChange={(e) => setCredForm({ ...credForm, password: e.target.value })} placeholder="Yeni şifre" data-testid="referee-cred-password" />
                      </div>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={autoGeneratePassword} className="w-full border-dashed" data-testid="referee-cred-generate-btn">
                      <Sparkles className="w-4 h-4 mr-1" /> Otomatik Şifre Oluştur (ör: {trSlug((selectedReferee?.name||'hakem').split(' ')[0]) || 'hakem'}123)
                    </Button>
                    <Button onClick={saveCredentials} disabled={savingCred} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="referee-cred-save-btn">
                      {savingCred ? 'Kaydediliyor...' : (selectedReferee.username ? 'Giriş Bilgilerini Güncelle' : 'Giriş Bilgilerini Oluştur')}
                    </Button>
                  </CardContent></Card>

                  {/* Aktif / Pasif durumu */}
                  <Card data-testid="referee-active-card"><CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">Hakem Durumu</p>
                        <p className="text-xs text-slate-500">Pasif hakemler atama listelerinde önerilmez.</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] ${refActive ? 'bg-emerald-500 hover:bg-emerald-500' : 'bg-slate-400 hover:bg-slate-400'}`}>
                          {refActive ? 'Aktif' : 'Pasif'}
                        </Badge>
                        <Button type="button" size="sm" variant={refActive ? 'outline' : 'default'} onClick={toggleActive} disabled={togglingActive}
                          className={refActive ? '' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="referee-active-toggle-btn">
                          {togglingActive ? '...' : (refActive ? 'Pasif Yap' : 'Aktif Yap')}
                        </Button>
                      </div>
                    </div>
                  </CardContent></Card>

                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => { setEditingReferee({...selectedReferee}); setShowDetailModal(false); setShowEditModal(true); }} className="flex-1" data-testid="detail-edit-referee-btn">
                      <Edit className="w-4 h-4 mr-1" /> Düzenle
                    </Button>
                    <Button variant="destructive" onClick={() => handleDelete(selectedReferee)} className="flex-1" data-testid="detail-delete-referee-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Sil
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============ COLLECT MODAL (Ödeme Yöntemi Seçimi) ============ */}
      <Dialog open={showCollectModal} onOpenChange={setShowCollectModal}>
        <DialogContent className="max-w-md" data-testid="collect-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Tahsilat Yap
            </DialogTitle>
            <DialogDescription>
              {collectTarget?.type === 'all'
                ? `${selectedRefereeData?.pending.length || 0} rezervasyonun tahsilatı yapılacak`
                : 'Bu rezervasyonun ödemesi alındı olarak işaretlenecek ve kasaya yazılacak'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-3">
                <p className="text-xs text-emerald-700 uppercase tracking-wide font-medium">Tahsil Edilecek Tutar</p>
                <p className="text-3xl font-bold text-emerald-900 mt-1">
                  {formatTL(
                    collectTarget?.type === 'all'
                      ? (selectedRefereeData?.totalPending || 0)
                      : (collectTarget?.reservation?.net_amount || 0)
                  )}
                </p>
              </CardContent>
            </Card>

            <div>
              <Label className="mb-2 block">Hangi Kasaya/Hesaba Girsin? *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { id: 'nakit', label: 'Nakit', icon: '💵' },
                  { id: 'garanti_bbva', label: 'Garanti BBVA', icon: '🏦' },
                  { id: 'bostanci', label: 'Bostancı', icon: '🏢' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCollectMethod(opt.id)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      collectMethod === opt.id
                        ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`collect-method-${opt.id}`}
                  >
                    <div className="text-xl mb-1">{opt.icon}</div>
                    <div>{opt.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowCollectModal(false)} className="flex-1">İptal</Button>
              <Button onClick={confirmCollect} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-collect-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Tahsil Et
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ============ HAVALE MODAL (Banka Hesabı Seçimi) ============ */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="max-w-md" data-testid="transfer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-blue-600" /> Havale ile Ödendi → Bankaya Çek
            </DialogTitle>
            <DialogDescription>
              Müşteri ödemeyi havale ile yaptı. Tam maç tutarı seçilen banka hesabına gelir olarak yazılacak, hakem ücreti ayrı gider olarak takip edilecek ve bu rezervasyon hakemin bekleyen listesinden çıkacak.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-3 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-700 uppercase tracking-wide font-medium">Bankaya Geçecek Tutar</span>
                  <span className="text-2xl font-bold text-blue-900">{formatTL(transferTarget?.reservation?.total_amount || 0)}</span>
                </div>
                {(transferTarget?.reservation?.referee_fee || 0) > 0 && (
                  <div className="flex justify-between items-center text-xs text-slate-600 pt-1 border-t border-blue-100">
                    <span>Hakem ücreti (ayrı gider olarak):</span>
                    <span className="font-semibold text-orange-700">-{formatTL(transferTarget?.reservation?.referee_fee || 0)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            <div>
              <Label className="mb-2 block">Hangi Banka Hesabına Geçti? *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { id: 'nakit', label: 'Nakit', icon: '💵' },
                  { id: 'garanti_bbva', label: 'Garanti BBVA', icon: '🏦' },
                  { id: 'bostanci', label: 'Bostancı', icon: '🏢' }
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTransferMethod(opt.id)}
                    className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      transferMethod === opt.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid={`transfer-method-${opt.id}`}
                  >
                    <div className="text-xl mb-1">{opt.icon}</div>
                    <div>{opt.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowTransferModal(false)} className="flex-1">İptal</Button>
              <Button onClick={confirmTransfer} disabled={transferringId} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="confirm-transfer-btn">
                <Landmark className="w-4 h-4 mr-1" /> Bankaya İşle
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RefereesTab;
